import type { ElementRect } from "./editorRelativeRect.ts";
import {
  handleAffectsHeight,
  handleAffectsWidth,
  isLeftSideHandle,
  isTopSideHandle,
  type ResizeHandle,
} from "./resizeDelta.ts";

/** A potential snap edge expressed as a 1D position with axis. */
export type SnapGuide = {
  position: number;
  orientation: "horizontal" | "vertical";
};

/** Pixel distance within which an edge will snap to a target. */
export const SNAP_THRESHOLD = 5;

/** Finds the closest snap target within threshold for a given edge position. */
const findBestSnap = (
  edgePosition: number,
  orientation: SnapGuide["orientation"],
  snapTargets: SnapGuide[],
): { offset: number; position: number } | undefined => {
  let best: { offset: number; position: number } | undefined;
  for (const target of snapTargets) {
    if (target.orientation !== orientation) continue;
    const offset = target.position - edgePosition;
    if (Math.abs(offset) <= SNAP_THRESHOLD && (!best || Math.abs(offset) < Math.abs(best.offset))) {
      best = { offset, position: target.position };
    }
  }
  return best;
};

export interface SnappedDeltaResult {
  delta: { x: number; y: number };
  snapGuides: SnapGuide[];
}

/**
 * Computes a snapped delta for resize operations.
 * Only the edges being moved by the resize handle are considered for snapping.
 * `rawDelta` and `nodeRect` must share a coordinate space (typically the
 * editor-relative screen space the snap targets are collected in).
 */
export const calculateResizeSnap = (
  nodeRect: ElementRect,
  handle: ResizeHandle,
  rawDelta: { x: number; y: number },
  snapTargets: SnapGuide[],
): SnappedDeltaResult => {
  const snapGuides: SnapGuide[] = [];
  const delta = { ...rawDelta };

  if (handleAffectsWidth(handle)) {
    const isLeftSide = isLeftSideHandle(handle);
    const movingEdgeX = isLeftSide ? nodeRect.left + rawDelta.x : nodeRect.left + nodeRect.width + rawDelta.x;

    const snap = findBestSnap(movingEdgeX, "vertical", snapTargets);
    if (snap) {
      delta.x += snap.offset;
      snapGuides.push({ position: snap.position, orientation: "vertical" });
    }
  }

  if (handleAffectsHeight(handle)) {
    const isTopSide = isTopSideHandle(handle);
    const movingEdgeY = isTopSide ? nodeRect.top + rawDelta.y : nodeRect.top + nodeRect.height + rawDelta.y;

    const snap = findBestSnap(movingEdgeY, "horizontal", snapTargets);
    if (snap) {
      delta.y += snap.offset;
      snapGuides.push({ position: snap.position, orientation: "horizontal" });
    }
  }

  return { delta, snapGuides };
};

const closestSnap = (
  a: { offset: number; position: number } | undefined,
  b: { offset: number; position: number } | undefined,
) => (a && b ? (Math.abs(a.offset) <= Math.abs(b.offset) ? a : b) : (a ?? b));

/**
 * Computes a snapped delta for position drag operations.
 * All four edges move uniformly — for each axis, considers both opposing edges
 * and snaps to whichever is closest to a target.
 */
export const calculatePositionSnap = (
  nodeRect: ElementRect,
  rawDelta: { x: number; y: number },
  snapTargets: SnapGuide[],
): SnappedDeltaResult => {
  const snapGuides: SnapGuide[] = [];
  const delta = { ...rawDelta };

  const leftSnap = findBestSnap(nodeRect.left + rawDelta.x, "vertical", snapTargets);
  const rightSnap = findBestSnap(nodeRect.left + nodeRect.width + rawDelta.x, "vertical", snapTargets);
  const bestX = closestSnap(leftSnap, rightSnap);
  if (bestX) {
    delta.x += bestX.offset;
    snapGuides.push({ position: bestX.position, orientation: "vertical" });
  }

  const topSnap = findBestSnap(nodeRect.top + rawDelta.y, "horizontal", snapTargets);
  const bottomSnap = findBestSnap(nodeRect.top + nodeRect.height + rawDelta.y, "horizontal", snapTargets);
  const bestY = closestSnap(topSnap, bottomSnap);
  if (bestY) {
    delta.y += bestY.offset;
    snapGuides.push({ position: bestY.position, orientation: "horizontal" });
  }

  return { delta, snapGuides };
};
